// shared/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import { useEffect, useRef } from 'react';
import type { SearchDeviceProps } from './searchDevice.types';

/**
 * Backs the device search box with its model filter and debounced text search. Seeds local state from the incoming props, resyncs when the parent search value changes, and reports the search text to the parent 500ms after typing stops, clearing the pending timer on the next change or on unmount.
 *
 * @param props - Initial search value, model and the parent change callback.
 * @returns Search box state and its change handler.
 */
export const useSearchDevice = (props: SearchDeviceProps) => {
  const updateSearchValueTimeout = useRef<NodeJS.Timeout | null>(null);

  const [state, setState] = useSetState({
    searchValue: props.searchValue || '',
    model: props.model || 'All models',
  });

  /**
   * Applies a change to the device search text or the model dropdown. Merges the named value into local state and forwards model changes to the parent right away, leaving the search text to be debounced.
   *
   * @param name - Field being changed, searchValue or model.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
    if (name === 'model') {
      props.onChange('model', value);
    }
  };

  useEffect(() => {
    if (props.searchValue !== state.searchValue) {
      setState({ searchValue: props.searchValue });
    }
  }, [props.searchValue]);

  useUpdateEffect(
    () => {
      if (updateSearchValueTimeout.current) {
        clearTimeout(updateSearchValueTimeout.current);
        updateSearchValueTimeout.current = null;
      }

      updateSearchValueTimeout.current = setTimeout(() => {
        props.onChange('searchValue', state.searchValue);
      }, 500);
    },
    [state.searchValue],
    () => {
      if (updateSearchValueTimeout.current) {
        clearTimeout(updateSearchValueTimeout.current);
        updateSearchValueTimeout.current = null;
      }
    },
  );

  return { state, onChange };
};
